"use client";

import { useState, type ReactNode } from "react";
import { cx } from "@frontend/components/ui";

type Tab = "chat" | "details";

/**
 * Shared room chrome: on phones, Chat / Details tabs (Chat first). On desktop
 * the same children sit in the multi-pane layout — one mount, no double polling.
 */
export function CollabRoomFrame({
  rail,
  header,
  chat,
  details,
}: {
  rail?: ReactNode;
  header: ReactNode;
  chat: ReactNode;
  details: ReactNode;
}) {
  const [tab, setTab] = useState<Tab>("chat");

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden rounded-xl border border-line bg-surface lg:flex-row">
      {rail}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="shrink-0">{header}</div>

        {/*
          Tabs sit above the chat/details stack with their own stacking context.
          display:contents + absolute message lists were swallowing taps on iOS.
        */}
        <div
          role="tablist"
          aria-label="Collab sections"
          className="relative z-20 flex shrink-0 border-b border-line bg-surface px-2 lg:hidden"
        >
          <TabButton
            id="chat"
            label="Chat"
            selected={tab === "chat"}
            onSelect={() => setTab("chat")}
          />
          <TabButton
            id="details"
            label="Details"
            selected={tab === "details"}
            onSelect={() => setTab("details")}
          />
        </div>

        <div className="relative z-0 flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          <div
            role="tabpanel"
            aria-label="Chat"
            className={cx(
              "min-h-0 min-w-0 flex-1 flex-col overflow-hidden",
              tab === "chat" ? "flex" : "hidden",
              "lg:flex",
            )}
          >
            {chat}
          </div>

          <aside
            role="tabpanel"
            aria-label="Details"
            className={cx(
              "min-h-0 w-full flex-col overflow-hidden lg:w-[19rem] lg:flex-none lg:border-l lg:border-line",
              tab === "details" ? "flex flex-1" : "hidden",
              "lg:flex",
            )}
          >
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
              {details}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function TabButton({
  id,
  label,
  selected,
  onSelect,
}: {
  id: Tab;
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      id={`collab-tab-${id}`}
      onClick={onSelect}
      className={cx(
        "relative z-20 -mb-px flex-1 touch-manipulation border-b-2 px-3 py-3 text-sm font-medium transition select-none",
        selected ? "border-ink text-ink" : "border-transparent text-ink-muted",
      )}
    >
      {label}
    </button>
  );
}
